/**
 * ApprovalActionsButtons Component
 *
 * Renders button variant of FAQ approval actions.
 * Extracted from ApprovalActions.tsx for better separation of concerns.
 *
 * Features:
 * - Submit button for draft FAQs
 * - Approve/Reject/Withdraw buttons for pending review FAQs
 * - Resubmit button for rejected FAQs
 * - Status info for pending FAQs (when user can't approve/reject)
 * - Integrated rejection dialog
 * - Loading states and tooltips
 *
 * Responsibilities:
 * - Renders all button variants based on FAQ approval status
 * - Manages rejection dialog state
 * - Calls mutation hooks
 * - Handles success/error callbacks
 *
 * Usage:
 * ```tsx
 * <ApprovalActionsButtons
 *   faq={faq}
 *   canApprove={isModerator}
 *   canReject={isModerator}
 *   canWithdraw={isOwner}
 *   size="small"
 *   onSuccess={(msg) => showSuccess(msg)}
 *   onError={(msg) => showError(msg)}
 * />
 * ```
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Send,
  Undo,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApprovalStatus, FAQDocumentResponse } from '../../api/data-contracts';
import {
  useSubmitFAQ,
  useApproveFAQ,
  useRejectFAQ,
  useWithdrawFAQ,
} from '../../hooks/api/useFAQs';
import { RejectionDialog } from './RejectionDialog';

interface ApprovalActionsButtonsProps {
  faq: FAQDocumentResponse;
  canApprove?: boolean;
  canReject?: boolean;
  canWithdraw?: boolean;
  canDelete?: boolean;
  size?: 'small' | 'medium' | 'large';
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onDelete?: (faq: FAQDocumentResponse) => void;
}

export const ApprovalActionsButtons: React.FC<ApprovalActionsButtonsProps> = ({
  faq,
  canApprove = false,
  canReject = false,
  canWithdraw = false,
  canDelete = false,
  size = 'small',
  onSuccess,
  onError,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Use factory hooks for mutations
  const submitMutation = useSubmitFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.submit_success'));
      },
      onError: () => {
        onError?.(t('faq.actions.submit_error'));
      },
    },
  });

  const approveMutation = useApproveFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.approve_success'));
      },
      onError: () => {
        onError?.(t('faq.actions.approve_error'));
      },
    },
  });

  const rejectMutation = useRejectFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.reject_success'));
        setRejectDialogOpen(false);
      },
      onError: () => {
        onError?.(t('faq.actions.reject_error'));
      },
    },
  });

  const withdrawMutation = useWithdrawFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.withdraw_success', 'FAQ submission withdrawn successfully'));
      },
      onError: () => {
        onError?.(t('faq.actions.withdraw_error', 'Failed to withdraw FAQ submission'));
      },
    },
  });

  const handleSubmitForApproval = () => {
    submitMutation.mutate(faq.document_id);
  };

  const handleApprove = () => {
    approveMutation.mutate(faq.document_id);
  };

  const handleReject = (reason: string) => {
    rejectMutation.mutate({ faqId: faq.document_id, reason });
  };

  const handleWithdraw = () => {
    withdrawMutation.mutate(faq.document_id);
  };

  const handleDelete = () => {
    onDelete?.(faq);
  };

  // Show delete button for approved/archived FAQs if user can delete
  if ((faq.approval_status === ApprovalStatus.Approved ||
      faq.approval_status === ApprovalStatus.Archived) && canDelete) {
    return (
      <Button
        size={size}
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={handleDelete}
      >
        {t('faq.actions.delete')}
      </Button>
    );
  }

  // Don't show actions if FAQ is already approved or archived
  if (faq.approval_status === ApprovalStatus.Approved ||
      faq.approval_status === ApprovalStatus.Archived) {
    return null;
  }

  // Show submit button for draft FAQs
  if (faq.approval_status === ApprovalStatus.Draft) {
    return (
      <Tooltip title={t('faq.actions.submit_tooltip')}>
        <Button
          size={size}
          variant="contained"
          color="primary"
          startIcon={<Send />}
          onClick={handleSubmitForApproval}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? t('common.submitting') : t('faq.actions.submit_for_approval')}
        </Button>
      </Tooltip>
    );
  }

  // Show approval actions for pending FAQs (if user has permissions)
  if (faq.approval_status === ApprovalStatus.PendingReview && (canApprove || canReject || canWithdraw)) {
    return (
      <>
        <Box display="flex" gap={1}>
          {canWithdraw && (
            <Button
              size={size}
              variant="outlined"
              color="warning"
              startIcon={<Undo />}
              onClick={handleWithdraw}
              disabled={withdrawMutation.isPending}
            >
              {t('faq.actions.withdraw', 'Withdraw')}
            </Button>
          )}
          {canApprove && (
            <Button
              size={size}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {t('faq.actions.approve')}
            </Button>
          )}
          {canReject && (
            <Button
              size={size}
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setRejectDialogOpen(true)}
              disabled={rejectMutation.isPending}
            >
              {t('faq.actions.reject')}
            </Button>
          )}
        </Box>

        <RejectionDialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          onConfirm={handleReject}
          faqId={faq.document_id}
          isSubmitting={rejectMutation.isPending}
        />
      </>
    );
  }

  // Show resubmit button for rejected FAQs
  if (faq.approval_status === ApprovalStatus.Rejected) {
    return (
      <Tooltip title={faq.rejection_reason || t('faq.actions.resubmit_tooltip')}>
        <Button
          size={size}
          variant="contained"
          color="warning"
          startIcon={<Send />}
          onClick={handleSubmitForApproval}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? t('common.submitting') : t('faq.actions.resubmit')}
        </Button>
      </Tooltip>
    );
  }

  // Show status info for pending review FAQs (for owners who can't approve/reject)
  if (faq.approval_status === ApprovalStatus.PendingReview && !canApprove && !canReject) {
    return (
      <Box display="flex" gap={1}>
        {canWithdraw && (
          <Button
            size={size}
            variant="outlined"
            color="warning"
            startIcon={<Undo />}
            onClick={handleWithdraw}
            disabled={withdrawMutation.isPending}
          >
            {t('faq.actions.withdraw', 'Withdraw')}
          </Button>
        )}
        <Tooltip title={t('faq.actions.pending_review_info')}>
          <Button
            size={size}
            variant="outlined"
            color="info"
            disabled
            startIcon={<Send />}
          >
            {t('faq.status.pending_review')}
          </Button>
        </Tooltip>
      </Box>
    );
  }

  return null;
};
